// „Schaden melden“ (Screen mieter/03) – die einzige Schreibaktion des Mieters. Erzeugt über
// portal_schaden_melden ein Ticket für die Verwaltung; es wird nichts verschickt.
// Am Handy als Blatt von unten, sonst mittig. Esc schließt, solange nicht gesendet wird.
import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import {
  Buildings, CameraPlus, CaretRight, Check, CircleNotch, DoorOpen, Key, Lightning, PaperPlaneTilt, Wrench, X,
} from '@phosphor-icons/react';
import { IconChip, farbKlasse } from '../../components/portal/bausteine';
import {
  KATEGORIEN, MAX_FOTOS, RAEUME, fotoProblem, pruefeMeldung, type Bereich, type KategorieKey,
} from '../../lib/portal/mieter';
import { schadenMelden } from '../../lib/portal/mieterApi';
import { KATEGORIE_STIL } from './symbole';

interface FotoEintrag { id: string; datei: File; url: string }

function Schalter({ an, onChange, icon: Symbol, farbe, titel, unter, trenner }: {
  an: boolean; onChange: (an: boolean) => void; icon: typeof Key; farbe: string; titel: string; unter: string; trenner: boolean;
}) {
  return (
    <button type="button" role="switch" aria-checked={an} onClick={() => onChange(!an)} data-tip={titel}
      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: 'none', borderTop: trenner ? '0.5px solid var(--p-w07)' : 'none',
        background: 'transparent', cursor: 'pointer', textAlign: 'left', color: 'var(--p-w)', width: '100%' }}>
      <Symbol size={19} weight="fill" style={{ color: farbe }} />
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span style={{ fontSize: 13.5, fontWeight: 800 }}>{titel}</span>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--p-w50)' }}>{unter}</span>
      </span>
      <span aria-hidden style={{ width: 50, height: 30, borderRadius: 15, flexShrink: 0, position: 'relative', transition: 'background .25s', background: an ? 'var(--p-schalter-an)' : 'var(--p-schalter-aus)' }}>
        <span style={{ position: 'absolute', top: 2, left: 2, width: 26, height: 26, borderRadius: 13, background: 'var(--p-w)', boxShadow: 'var(--p-knopf-sh)',
          transform: an ? 'translateX(20px)' : 'none', transition: 'transform .25s cubic-bezier(.3,1.4,.5,1)' }} />
      </span>
    </button>
  );
}

export default function SchadenMeldenSheet({ adresse, onSchliessen, onGesendet, onZuMeldungen }: {
  adresse: string;
  onSchliessen: () => void;
  /** nach erfolgreichem Senden: Liste neu laden */
  onGesendet: (ticketId: string) => void;
  onZuMeldungen: () => void;
}) {
  const [kategorie, setKategorie] = useState<KategorieKey | null>(null);
  const [bereich, setBereich] = useState<Bereich>('wohnung');
  const [raum, setRaum] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [fotos, setFotos] = useState<FotoEintrag[]>([]);
  const [ziehen, setZiehen] = useState(false);
  const [zutritt, setZutritt] = useState(true);
  const [dringend, setDringend] = useState(false);
  const [versucht, setVersucht] = useState(false);
  const [wackeln, setWackeln] = useState(0);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [fertig, setFertig] = useState<{ nummer: string; fotosFehlgeschlagen: number } | null>(null);
  const dateiRef = useRef<HTMLInputElement>(null);
  const ersterRef = useRef<HTMLButtonElement>(null);
  const pruefung = pruefeMeldung({ kategorie, bereich, raum, beschreibung: text });
  const fehlerKat = versucht && !pruefung.kategorie;
  const fehlerText = versucht && !pruefung.beschreibung;

  // Hintergrund sperren, Esc schließt, Fokus auf die erste Kachel
  useEffect(() => {
    const vorher = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    ersterRef.current?.focus();
    return () => { document.body.style.overflow = vorher; };
  }, []);
  useEffect(() => {
    const taste = (e: KeyboardEvent) => { if (e.key === 'Escape' && !sendet) onSchliessen(); };
    document.addEventListener('keydown', taste);
    return () => document.removeEventListener('keydown', taste);
  }, [sendet, onSchliessen]);
  // Vorschau-URLs beim Schließen freigeben (einzelne beim Entfernen)
  const fotosRef = useRef<FotoEintrag[]>([]);
  fotosRef.current = fotos;
  useEffect(() => () => { fotosRef.current.forEach((f) => URL.revokeObjectURL(f.url)); }, []);
  const fotoWeg = (id: string) => setFotos((l) => {
    const weg = l.find((x) => x.id === id);
    if (weg) URL.revokeObjectURL(weg.url);
    return l.filter((x) => x.id !== id);
  });

  const fotosHinzu = (liste: FileList | null) => {
    if (!liste) return;
    const neu: FotoEintrag[] = [];
    const probleme: string[] = [];
    for (const d of Array.from(liste)) {
      const p = fotoProblem(d, fotos.length + neu.length);
      if (p) { probleme.push(p); continue; }
      neu.push({ id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, datei: d, url: URL.createObjectURL(d) });
    }
    setFotos((f) => [...f, ...neu]);
    setFehler(probleme.length ? [...new Set(probleme)].join(' ') : null);
    setZiehen(false);
  };

  const senden = async () => {
    if (sendet) return;
    if (!pruefung.ok || !kategorie) { setVersucht(true); setWackeln((w) => w + 1); return; }
    setSendet(true);
    setFehler(null);
    try {
      const r = await schadenMelden({ kategorie, bereich, raum, beschreibung: text, zutritt, dringend, fotos: fotos.map((f) => f.datei) });
      setFertig({ nummer: r.ticketNummer, fotosFehlgeschlagen: r.fotosFehlgeschlagen });
      onGesendet(r.ticketId);
    } catch (e) {
      setFehler((e as Error).message || 'Die Meldung konnte nicht gesendet werden. Bitte versuchen Sie es noch einmal.');
    } finally {
      setSendet(false);
    }
  };

  const fussText = useMemo(() => {
    if (versucht && !pruefung.ok) return 'Bitte die markierten Angaben ergänzen';
    if (fotos.length) return `${fotos.length} Foto${fotos.length > 1 ? 's' : ''} angehängt`;
    return 'Fotos helfen uns sehr';
  }, [versucht, pruefung.ok, fotos.length]);

  const wahlKat = (k: KategorieKey) => {
    setKategorie(k);
    if (k === 'gemein' && bereich !== 'gemeinschaft') { setBereich('gemeinschaft'); setRaum(null); }
  };

  const ziehenEvents = {
    onDragOver: (e: DragEvent) => { e.preventDefault(); if (!ziehen) setZiehen(true); },
    onDragLeave: () => setZiehen(false),
    onDrop: (e: DragEvent) => { e.preventDefault(); fotosHinzu(e.dataTransfer.files); },
  };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="schaden-titel" className="mp-sheet-huelle"
      style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', justifyContent: 'center' }}>
      <div onClick={() => !sendet && onSchliessen()} aria-hidden
        style={{ position: 'absolute', inset: 0, background: 'var(--p-sheet-schleier)', WebkitBackdropFilter: 'blur(18px) saturate(120%)', backdropFilter: 'blur(18px) saturate(120%)' }} />
      <div className="mp-sheet p-blatt" style={{ position: 'relative', width: '100%', maxWidth: 680, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--p-sheet)', boxShadow: 'var(--p-sheet-sh)' }}>
        <div aria-hidden className="p-farbe-heizung" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(70% 40% at 100% 0%, rgba(var(--rgb),0.2), rgba(var(--rgb),0) 70%)' }} />
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12, padding: '16px 16px 12px 18px' }}>
          <IconChip farbe="heizung" icon={Wrench} groesse={40} radius={12} />
          <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <span id="schaden-titel" style={{ fontSize: 18, fontWeight: 800 }}>Schaden melden</span>
            <span className="p-kuerzen" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--p-w50)' }}>{adresse}</span>
          </span>
          <button type="button" className="p-rund" onClick={onSchliessen} disabled={sendet} data-tip="Schließen ohne zu senden" aria-label="Schließen ohne zu senden" style={{ width: 36, height: 36, color: 'var(--p-w70)' }}>
            <X size={16} weight="bold" />
          </button>
        </div>

        {fertig ? (
          <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '30px 24px 26px', textAlign: 'center' }} role="status">
            <span className="p-farbe-konto p-chip" style={{ width: 76, height: 76, borderRadius: 38 }}><Check size={38} weight="bold" /></span>
            <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.015em' }}>Meldung {fertig.nummer} ist eingegangen</span>
            <span style={{ maxWidth: 420, fontSize: 13.5, fontWeight: 600, lineHeight: 1.5, color: 'var(--p-w60)' }}>
              Die Verwaltung kümmert sich darum. Unter „Meldungen“ sehen Sie jederzeit den Stand und ob ein Termin vereinbart wurde.
            </span>
            {fertig.fotosFehlgeschlagen > 0 && (
              <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--p-fehler-text)' }}>
                {fertig.fotosFehlgeschlagen === 1 ? '1 Foto konnte' : `${fertig.fotosFehlgeschlagen} Fotos konnten`} nicht übertragen werden. Die Meldung selbst ist angekommen.
              </span>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', paddingTop: 6 }}>
              <button type="button" className="p-knopf" onClick={onSchliessen} style={{ height: 42, border: 'none', background: 'var(--p-w08)' }}>Fertig</button>
              <button type="button" className="p-knopf p-knopf-gold" onClick={onZuMeldungen} data-tip="Zur Liste der eigenen Meldungen" style={{ height: 42 }}>
                Zu meinen Meldungen <CaretRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ position: 'relative', flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', padding: '4px 18px 18px', display: 'flex', flexDirection: 'column', gap: 18 }}>
              <fieldset style={{ border: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <legend className="mp-label" style={{ color: fehlerKat ? 'var(--p-fehler-text)' : undefined, marginBottom: 10 }}>Was ist betroffen? *</legend>
                <div key={fehlerKat ? `k${wackeln}` : 'k'} className={`mp-kacheln ${fehlerKat ? 'p-wackeln' : ''}`} role="radiogroup" aria-label="Was ist betroffen?" aria-invalid={fehlerKat}>
                  {KATEGORIEN.map((k, i) => {
                    const an = kategorie === k.key;
                    const stil = KATEGORIE_STIL[k.key];
                    return (
                      <button key={k.key} ref={i === 0 ? ersterRef : undefined} type="button" role="radio" aria-checked={an} data-tip={k.label} onClick={() => wahlKat(k.key)}
                        className={farbKlasse(stil.farbe)}
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '14px 6px 12px', borderRadius: 18, border: 'none', cursor: 'pointer', color: 'var(--p-w)',
                          background: an ? 'var(--p-gold-fl)' : 'var(--p-w05)',
                          boxShadow: an ? 'var(--p-gold-ring)' : fehlerKat ? 'var(--p-fehler-ring)' : 'inset 0 0 0 0.5px var(--p-w06)' }}>
                        <IconChip farbe={stil.farbe} icon={stil.icon} groesse={42} radius={13} />
                        <span style={{ fontSize: 12, fontWeight: 800, textAlign: 'center', lineHeight: 1.2 }}>{k.label}</span>
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <span className="mp-label">Wo?</span>
                <div role="radiogroup" aria-label="Wo?" style={{ display: 'flex', gap: 2, padding: 3, borderRadius: 19, background: 'var(--p-s28)' }}>
                  {([['wohnung', 'In meiner Wohnung', DoorOpen], ['gemeinschaft', 'Gemeinschaftsfläche', Buildings]] as const).map(([k, l, Symbol]) => {
                    const an = bereich === k;
                    return (
                      <button key={k} type="button" role="radio" aria-checked={an} onClick={() => { setBereich(k); setRaum(null); }}
                        style={{ flex: 1, minWidth: 0, height: 34, borderRadius: 16, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                          background: an ? 'var(--p-w14)' : 'transparent', color: an ? 'var(--p-w)' : 'var(--p-w55)' }}>
                        <Symbol size={15} weight={an ? 'fill' : 'regular'} /><span className="p-kuerzen">{l}</span>
                      </button>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} role="radiogroup" aria-label="Raum">
                  {RAEUME[bereich].map((r) => {
                    const an = raum === r;
                    return (
                      <button key={r} type="button" role="radio" aria-checked={an} onClick={() => setRaum(an ? null : r)}
                        style={{ height: 34, padding: '0 13px', borderRadius: 17, border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 800,
                          background: an ? 'var(--p-gold-fl2)' : 'var(--p-w06)', color: an ? 'var(--p-gold-text-hell)' : 'var(--p-w75)', boxShadow: an ? 'var(--p-gold-ring-leise)' : 'none' }}>
                        {r}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label htmlFor="schaden-text" className="mp-label" style={{ color: fehlerText ? 'var(--p-fehler-text)' : undefined }}>Was ist passiert? *</label>
                <textarea
                  id="schaden-text" key={fehlerText ? `t${wackeln}` : 't'} className={fehlerText ? 'p-wackeln' : ''}
                  value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={4000} aria-invalid={fehlerText} aria-describedby="schaden-hinweis"
                  placeholder="z. B. Der Heizkörper im Bad bleibt seit Montag kalt, das Ventil lässt sich nicht drehen."
                  style={{ width: '100%', resize: 'vertical', minHeight: 104, padding: '13px 14px', borderRadius: 16, border: 'none', outline: 'none', fontSize: 16, fontWeight: 600, lineHeight: 1.5, color: 'var(--p-w)',
                    background: 'var(--p-s28)', boxShadow: fehlerText ? 'inset 0 0 0 1.5px var(--p-fehler)' : 'inset 0 0 0 0.5px var(--p-w06)' }}
                />
                <span id="schaden-hinweis" style={{ fontSize: 11.5, fontWeight: 700, color: fehlerText ? 'var(--p-fehler-text)' : 'var(--p-w40)' }}>
                  {fehlerText ? 'Bitte beschreiben Sie den Schaden in ein paar Worten (mindestens 10 Zeichen).' : 'Je genauer, desto schneller kann der richtige Handwerker kommen.'}
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span className="mp-label">Fotos</span>
                <button type="button" onClick={() => dateiRef.current?.click()} {...ziehenEvents} disabled={fotos.length >= MAX_FOTOS}
                  data-tip="Fotos hierher ziehen oder klicken, um auszuwählen"
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 130, padding: 18, borderRadius: 18, cursor: 'pointer', textAlign: 'center', color: 'var(--p-w)',
                    background: ziehen ? 'var(--p-gold-fl)' : 'var(--p-s22)', border: `1.5px dashed ${ziehen ? 'var(--p-gold-text)' : 'var(--p-w15)'}`, transform: ziehen ? 'scale(1.01)' : 'none', transition: 'background .2s, border-color .2s, transform .2s' }}>
                  <span style={{ width: 46, height: 46, borderRadius: 23, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--p-w08)' }}>
                    <CameraPlus size={22} weight="bold" style={{ color: 'var(--p-gold-text)' }} />
                  </span>
                  <span style={{ fontSize: 13.5, fontWeight: 800 }}>{ziehen ? 'Loslassen zum Hinzufügen' : fotos.length ? 'Weitere Fotos hinzufügen' : 'Fotos hierher ziehen oder antippen'}</span>
                  <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--p-w45)' }}>JPG, PNG oder HEIC · bis 10 MB je Foto</span>
                </button>
                <input ref={dateiRef} type="file" accept="image/*,.heic,.heif" multiple style={{ display: 'none' }} aria-label="Fotos auswählen"
                  onChange={(e) => { fotosHinzu(e.target.files); e.target.value = ''; }} />
                {fotos.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))', gap: 8 }}>
                    {fotos.map((f) => (
                      <div key={f.id} style={{ position: 'relative', aspectRatio: '1', borderRadius: 14, overflow: 'hidden', background: 'var(--p-w08)' }}>
                        <img src={f.url} alt={f.datei.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                        <button type="button" onClick={() => fotoWeg(f.id)} data-tip="Foto entfernen" aria-label={`${f.datei.name} entfernen`}
                          style={{ position: 'absolute', top: 5, right: 5, width: 24, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--p-s60)', color: 'var(--p-w)' }}>
                          <X size={12} weight="bold" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', borderRadius: 18, background: 'var(--p-w05)', overflow: 'hidden' }}>
                <Schalter an={zutritt} onChange={setZutritt} icon={Key} farbe="var(--p-gold-text)" titel="Zutritt in meiner Abwesenheit erlaubt"
                  unter="Hausmeister oder Handwerker dürfen die Wohnung mit Schlüssel betreten" trenner={false} />
                <Schalter an={dringend} onChange={setDringend} icon={Lightning} farbe="var(--p-warn-punkt)" titel="Dringend"
                  unter="z. B. kein warmes Wasser oder Heizung fällt im Winter aus" trenner />
              </div>

              {fehler && <span role="alert" style={{ fontSize: 13, fontWeight: 700, color: 'var(--p-fehler-text)' }}>{fehler}</span>}
            </div>

            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))', borderTop: '0.5px solid var(--p-w08)', background: 'var(--p-sheet-fuss)', flexWrap: 'wrap' }}>
              <span style={{ flex: '1 1 140px', fontSize: 12, fontWeight: 700, color: versucht && !pruefung.ok ? 'var(--p-fehler-text)' : 'var(--p-w45)' }}>{fussText}</span>
              <span style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                <button type="button" className="p-knopf" onClick={onSchliessen} disabled={sendet} data-tip="Abbrechen ohne zu senden" style={{ height: 42, border: 'none', background: 'var(--p-w08)' }}>Abbrechen</button>
                <button type="button" className="p-knopf p-knopf-gold" onClick={() => void senden()} disabled={sendet} data-tip="Meldung an die Verwaltung senden" style={{ height: 42 }}>
                  {sendet ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : <PaperPlaneTilt size={16} weight="bold" />}
                  {sendet ? 'Wird gesendet …' : 'Meldung senden'}
                </button>
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
